/*
  JapanSolved™ Icon Brain v4.1
  Mockup-Soul / site-wide pre-ponder wired / sentence-exact / hierarchy-aware / registry-ranked icon controller.

  Requires:
  - window.JapanSolvedIconRegistry.icons
  - window.JapanSolvedIconLinkMap (optional)
  - window.JapanSolvedIconDesignMind (recommended)
  - window.JapanSolvedIconPageIntentMapSitewide (recommended, page choreography memory)
*/
(function () {
  'use strict';

  var CONFIG = window.JSI_ICON_BRAIN_CONFIG || {};
  var REGISTRY = window.JapanSolvedIconRegistry || { icons: [] };
  var LINK_MAP = window.JapanSolvedIconLinkMap || {};
  var MIND = window.JapanSolvedIconDesignMind || { sectionBlueprints: {}, semanticLexicon: {} };
  var PAGE_MAP = window.JapanSolvedIconPageIntentMapSitewide || window.JapanSolvedIconPageIntentMap || {};
  var PAGE_MAP_VERSION = (PAGE_MAP && (PAGE_MAP.version || PAGE_MAP._version)) || 'sitewide-v1';
  var ICONS = Array.isArray(REGISTRY.icons) ? REGISTRY.icons : [];
  var SELECTOR = '[data-jsi-call], [data-jsi-icon-call], .jsi-icon-slot, .jsi-icon-group';
  var debugMode = Boolean(CONFIG.debug) || /(?:\?|&)jsi_debug=1(?:&|$)/.test(window.location.search);

  var usedIconIds = new Set();
  var usedFamilyKeys = new Set();
  var usedRotationGroups = new Set();
  var usedVisualSubjects = new Set();

  function slug(v) {
    return String(v || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/™/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function words(v) {
    return String(v || '')
      .toLowerCase()
      .replace(/™/g, '')
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  }

  function tokenList(v) {
    if (!v) return [];
    if (Array.isArray(v)) return v.flatMap(tokenList);
    return String(v).split(/[;,|]+/).flatMap(function (part) {
      var s = slug(part);
      var ws = words(part);
      return [s].concat(ws);
    }).filter(Boolean);
  }

  function uniq(arr) {
    return Array.from(new Set(arr.filter(Boolean)));
  }

  function normalizeKeyName(k) {
    return String(k || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function getField(icon, keys) {
    if (!icon) return '';
    for (var i=0; i<keys.length; i++) {
      var key = keys[i];
      if (icon[key] !== undefined && icon[key] !== null && icon[key] !== '') return icon[key];
    }
    // Registry v1 is camelCase. Earlier ledgers used spaced column labels.
    // This normalized fallback lets "Primary Category" find primaryCategory, etc.
    var normLookup = {};
    Object.keys(icon).forEach(function (k) { normLookup[normalizeKeyName(k)] = k; });
    for (var j=0; j<keys.length; j++) {
      var nk = normalizeKeyName(keys[j]);
      var real = normLookup[nk];
      if (real && icon[real] !== undefined && icon[real] !== null && icon[real] !== '') return icon[real];
    }
    return '';
  }

  function iconId(icon) {
    return String(getField(icon, ['id','icon_id','Icon ID','Registry ID','RegistryID']) || getField(icon, ['Normalized Handle','Final File Name / Handle','filename','handle']) || '');
  }

  function iconUrl(icon) {
    return String(getField(icon, ['url','shopify_cdn_url','Shopify CDN URL','URL']) || '');
  }

  function familyKey(icon) {
    return String(getField(icon, ['familyKey','Controller Family Key','family_id','Asset Family','assetFamily']) || '');
  }

  function rotationGroup(icon) {
    return String(getField(icon, ['rotationGroup','Rotation Group','rotation_group']) || familyKey(icon));
  }

  function iconRank(icon) {
    var rank = Number(getField(icon, ['familyCategoryRank','designFamilyRank','Family Category Rank','Design Family Rank','family_rank','rank']) || 99);
    if (!isFinite(rank)) rank = 99;
    return rank;
  }

  function priority(icon) {
    var score = Number(getField(icon, ['defaultPriorityScore','Default Priority Score','priority','premium_score']) || 50);
    return isFinite(score) ? score : 50;
  }

  function pagePath() {
    return String(window.location.pathname || '').replace(/\/$/, '');
  }

  function isSelfLink(href) {
    if (!href) return false;
    try {
      var u = new URL(href, window.location.origin);
      return u.pathname.replace(/\/$/, '') === pagePath();
    } catch (e) { return false; }
  }


  function currentPageHandle() {
    var path = pagePath();
    var parts = path.split('/').filter(Boolean);
    return slug(parts[parts.length - 1] || '');
  }

  function activePageRecord() {
    var handle = currentPageHandle();
    if (PAGE_MAP && PAGE_MAP[handle]) return PAGE_MAP[handle];
    // Soft fallback for maps keyed by content_id or canonical_handle.
    var found = null;
    Object.keys(PAGE_MAP || {}).some(function (k) {
      var rec = PAGE_MAP[k] || {};
      if (slug(rec.canonical_handle) === handle || slug(rec.content_id) === handle) { found = rec; return true; }
      return false;
    });
    return found || null;
  }

  function zoneKey(v) { return slug(v); }

  function findPageZone(page, ds, section) {
    if (!page || !Array.isArray(page.zones)) return null;
    var wanted = zoneKey(
      ds.jsiZone || ds.jsiSectionKey || ds.zone || ds.sectionKey ||
      ds.jsiIntentZone || ds.iconZone || ds.jsiSection || ds.section || section
    );
    if (!wanted) return null;
    var exact = null;
    page.zones.some(function (z) {
      if (zoneKey(z.section_key) === wanted) { exact = z; return true; }
      return false;
    });
    if (exact) return exact;

    // Friendly partial match: "architecture-door-card" may find "architecture_doors".
    page.zones.some(function (z) {
      var zk = zoneKey(z.section_key);
      if (zk && (wanted.indexOf(zk) >= 0 || zk.indexOf(wanted) >= 0)) { exact = z; return true; }
      return false;
    });
    return exact;
  }

  function zoneHasNoIcon(page, zone, key) {
    if (!page || !Array.isArray(page.no_icon_zones)) return false;
    var zkey = zoneKey(key || (zone && zone.section_key) || '');
    return page.no_icon_zones.some(function (item) {
      if (typeof item === 'string') return zoneKey(item) === zkey;
      return zoneKey(item.section_key || item.zone || item.name || '') === zkey;
    });
  }

  function midSizeFromRange(v) {
    var s = String(v || '');
    var m = s.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*px/i);
    if (m) return Math.round((Number(m[1]) + Number(m[2])) / 2) + 'px';
    m = s.match(/(\d+(?:\.\d+)?)\s*px/i);
    return m ? m[1] + 'px' : '';
  }

  function zoneCandidates(zone) {
    if (!zone || !Array.isArray(zone.candidate_registry_ids)) return [];
    var out = [];
    zone.candidate_registry_ids.forEach(function (c) {
      if (!c) return;
      if (typeof c === 'string') out.push(slug(c));
      else {
        out.push(slug(c.registry_id));
        out.push(slug(c.handle));
        out.push(slug(c.label));
        out.push(slug(c.primary_category));
      }
    });
    return uniq(out);
  }

  function expandSemanticTokens(value) {
    var out = tokenList(value);
    var lex = MIND.semanticLexicon || {};
    tokenList(value).forEach(function (t) {
      if (lex[t]) out = out.concat(tokenList(lex[t]));
    });
    return uniq(out);
  }

  function readContext(el) {
    var ds = el.dataset || {};
    var page = activePageRecord();
    var rawSection = ds.jsiSection || ds.section || ds.jsiZone || ds.jsiSectionKey || 'category-grid-card';
    var section = slug(rawSection);
    var zone = findPageZone(page, ds, section);
    var blueprint = (MIND.sectionBlueprints || {})[section] || {};

    function z(key, fallback) {
      return zone && zone[key] !== undefined && zone[key] !== null && zone[key] !== '' ? zone[key] : fallback;
    }
    function sketch(key, fallback) {
      var s = zone && zone.icon_call_attribute_sketch ? zone.icon_call_attribute_sketch : {};
      var attr = 'data-jsi-' + key.replace(/[A-Z]/g, function (m) { return '-' + m.toLowerCase(); });
      return s[attr] || s[key] || fallback;
    }

    var exactNeeds = zone && Array.isArray(zone.exact_icon_needs) ? zone.exact_icon_needs.join(', ') : '';
    var preferredSize = ds.jsiSize || blueprint.size || midSizeFromRange(z('size_range', ''));
    var inferredCall = [ds.jsiObject, ds.jsiAction, ds.jsiConcept, ds.jsiEmotion, ds.jsiTopic, exactNeeds, ds.jsiCall, rawSection].filter(Boolean).join(' ');

    var ctx = {
      call: slug(ds.jsiCall || ds.jsiIconCall || ds.call || ds.iconCall || inferredCall || blueprint.call || ''),
      section: section,
      zoneKey: zoneKey(zone && zone.section_key || ds.jsiZone || ds.jsiSectionKey || ''),
      pageHandle: page ? slug(page.canonical_handle || page.content_id || currentPageHandle()) : currentPageHandle(),
      pageTitle: page ? (page.title || '') : '',
      narrativeRole: page ? (page.narrative_role || '') : '',
      iconStrategySummary: page ? (page.icon_strategy_summary || '') : '',
      category: ds.jsiCategory || ds.category || '',
      pillars: ds.jsiPillars || ds.pillars || '',
      pageWing: ds.jsiPageWing || ds.pageWing || (page && page.page_wing) || '',
      style: ds.jsiStyle || ds.style || (page && page.style_mood) || '',
      fill: ds.jsiFill || ds.fill || '',
      density: ds.jsiDensity || ds.density || '',
      mood: ds.jsiMood || ds.mood || '',
      action: ds.jsiAction || '',
      object: ds.jsiObject || ds.jsiSymbol || '',
      concept: ds.jsiConcept || '',
      emotion: ds.jsiEmotion || '',
      audience: ds.jsiAudience || '',
      gap: ds.jsiGap || '',
      intent: ds.jsiIntent || '',
      stage: ds.jsiStage || '',
      tier: ds.jsiTier || '',
      topic: ds.jsiTopic || exactNeeds || '',
      destination: ds.jsiDestination || '',
      visual: ds.jsiVisual || '',
      exactIconNeeds: exactNeeds,
      hierarchy: slug(ds.jsiHierarchy || sketch('hierarchy', z('hierarchy', blueprint.hierarchy || 'mid'))),
      role: slug(ds.jsiRole || sketch('role', z('icon_role', blueprint.role || 'category-marker'))),
      placement: slug(ds.jsiPlacement || sketch('placement', z('preferred_placement', blueprint.placement || 'top-center-free'))),
      container: slug(ds.jsiContainer || sketch('container', z('preferred_container', blueprint.container || 'none'))),
      treatment: slug(ds.jsiTreatment || sketch('treatment', z('preferred_treatment', blueprint.treatment || 'support'))),
      visualOwner: slug(ds.jsiVisualOwner || sketch('visualOwner', z('visual_owner', blueprint.visualOwner || 'title'))),
      size: preferredSize || '',
      opacity: ds.jsiOpacity || blueprint.opacity || '',
      strictness: slug(ds.jsiStrictness || sketch('strictness', blueprint.strictness || (zone ? 'exact' : 'strong'))),
      count: Math.max(1, parseInt(ds.jsiCount || ds.count || '1', 10) || 1),
      rotation: slug(ds.jsiRotation || 'stable'),
      noRepeat: String(ds.jsiNoRepeat || 'true') !== 'false',
      avoidFamilyRepeat: String(ds.jsiAvoidFamilyRepeat || 'true') !== 'false',
      clickable: String(ds.jsiClickable || (zone && zone.clickable ? 'true' : 'false')) === 'true',
      href: ds.jsiHref || '',
      linkKey: ds.jsiLinkKey || '',
      linkMode: ds.jsiLinkMode || '',
      linkLabel: ds.jsiLinkLabel || ds.jsiAlt || ds.jsiTitle || '',
      alt: ds.jsiAlt || '',
      title: ds.jsiTitle || '',
      renderTone: slug(ds.jsiRenderTone || 'auto'),
      zoneCandidates: zoneCandidates(zone),
      noIconFromMap: zoneHasNoIcon(page, zone, zone && zone.section_key || rawSection)
    };
    if (!ctx.call) ctx.call = slug([ctx.object, ctx.action, ctx.concept, ctx.topic, ctx.category].filter(Boolean).join(' '));
    return ctx;
  }

  function requestTokens(ctx) {
    var high = [];
    var medium = [];
    var low = [];
    high = high.concat(expandSemanticTokens(ctx.object));
    high = high.concat(expandSemanticTokens(ctx.action));
    high = high.concat(expandSemanticTokens(ctx.concept));
    high = high.concat(expandSemanticTokens(ctx.emotion));
    high = high.concat(expandSemanticTokens(ctx.audience));
    high = high.concat(expandSemanticTokens(ctx.gap));
    high = high.concat(expandSemanticTokens(ctx.intent));
    high = high.concat(expandSemanticTokens(ctx.stage));
    high = high.concat(expandSemanticTokens(ctx.topic));
    high = high.concat(expandSemanticTokens(ctx.exactIconNeeds));
    high = high.concat(expandSemanticTokens(ctx.call));
    medium = medium.concat(tokenList(ctx.category));
    medium = medium.concat(tokenList(ctx.pillars));
    medium = medium.concat(tokenList(ctx.pageWing));
    medium = medium.concat(tokenList(ctx.narrativeRole));
    medium = medium.concat(tokenList(ctx.iconStrategySummary));
    medium = medium.concat(tokenList(ctx.visual));
    low = low.concat(tokenList(ctx.style));
    low = low.concat(tokenList(ctx.fill));
    low = low.concat(tokenList(ctx.density));
    low = low.concat(tokenList(ctx.mood));
    low = low.concat(tokenList(ctx.section));
    return { high: uniq(high), medium: uniq(medium), low: uniq(low), all: uniq(high.concat(medium, low)) };
  }

  function iconTokens(icon) {
    var fields = [
      'id','filename','handle','cleanLabel','primaryCategory','categoryTokens','purposeTags','visualTags','sectionFit','rawBestSectionUse','rotationGroup',
      'assetFamily','familyKey','fillLevel','visualDensity','lineWeight','styleFamily','solutionPillars','pageWingFit',
      'darkThemeFit','lightThemeFit','variantRole','notes','searchTokens',
      'Final File Name / Handle','Normalized Handle','Clean Icon Label','Primary Category','Purpose Tags','Visual Tags','Best Section Use','Rotation Group',
      'Asset Family','Controller Family Key','Fill Level','Visual Density','Line Weight','Style Family','Solution Pillars','Page Wing Fit',
      'Dark Theme Fit','Light Theme Fit','Normalized Section Fit','Variant Role','Notes'
    ];
    var out = [];
    fields.forEach(function (k) { out = out.concat(tokenList(getField(icon, [k]))); });
    return uniq(out);
  }

  var tokenCache = new WeakMap();
  function getIconTokens(icon) {
    if (tokenCache.has(icon)) return tokenCache.get(icon);
    var t = iconTokens(icon);
    tokenCache.set(icon, t);
    return t;
  }

  function matchesAny(tokens, hay) {
    var set = new Set(hay);
    var n = 0;
    tokens.forEach(function (t) { if (set.has(t)) n++; });
    return n;
  }

  function scoreIcon(icon, ctx, alreadyPicked) {
    var url = iconUrl(icon);
    if (!url) return -99999;
    var it = getIconTokens(icon);
    var req = requestTokens(ctx);
    var score = 0;
    var idSlug = slug(iconId(icon));
    var handleSlug = slug(getField(icon, ['handle','Normalized Handle','Final File Name / Handle']));
    var primarySlug = slug(getField(icon, ['primaryCategory','Primary Category']));

    var highHits = matchesAny(req.high, it);
    var medHits = matchesAny(req.medium, it);
    var lowHits = matchesAny(req.low, it);
    var candidateHit = ctx.zoneCandidates && ctx.zoneCandidates.length && (ctx.zoneCandidates.indexOf(idSlug) >= 0 || ctx.zoneCandidates.indexOf(handleSlug) >= 0 || ctx.zoneCandidates.indexOf(primarySlug) >= 0);

    score += candidateHit ? 80 : 0;
    score += highHits * 22;
    score += medHits * 8;
    score += lowHits * 3;
    score += priority(icon) * 0.7;
    score += Math.max(0, 14 - iconRank(icon)) * 1.6;

    // Strict sentence-exact penalty: exact calls should not coast on generic page-wing matches.
    if (ctx.strictness === 'exact' && highHits === 0) score -= 55;
    if (ctx.strictness === 'strong' && highHits === 0 && medHits < 2) score -= 26;

    // Theme safety bonus.
    var darkFit = slug(getField(icon, ['Dark Theme Fit']));
    var lightFit = slug(getField(icon, ['Light Theme Fit']));
    if (darkFit.indexOf('excellent') >= 0) score += 7;
    if (lightFit.indexOf('excellent') >= 0) score += 4;
    if (darkFit.indexOf('plate') >= 0 && ctx.container === 'none' && ctx.hierarchy === 'senpai') score -= 4;

    // Visual treatment / fill fit.
    var fill = slug(getField(icon, ['Fill Level']));
    if (ctx.treatment === 'ghost' && fill.indexOf('outline') >= 0) score += 8;
    if (ctx.hierarchy === 'micro' && String(getField(icon, ['Variant Role'])).toLowerCase().indexOf('micro') >= 0) score += 10;
    if (ctx.hierarchy === 'senpai' && String(getField(icon, ['Visual Density'])).toLowerCase().indexOf('minimal') >= 0) score += 5;

    var id = iconId(icon);
    var fam = familyKey(icon);
    var rot = rotationGroup(icon);
    if (ctx.noRepeat && (usedIconIds.has(id) || alreadyPicked.has(id))) score -= 999;
    if (ctx.avoidFamilyRepeat && usedFamilyKeys.has(fam)) score -= 30;
    if (ctx.avoidFamilyRepeat && usedRotationGroups.has(rot)) score -= 20;
    if (alreadyPicked.has(fam)) score -= 45;
    if (usedVisualSubjects.has(slug(getField(icon, ['Primary Category'])))) score -= 4;

    // Stable variation seed gives nearby ties a consistent but not random result.
    score += seededJitter(iconId(icon), ctx) * 1.2;
    return score;
  }

  function seededJitter(seed, ctx) {
    var s = [window.location.pathname, ctx.section, ctx.call, seed, REGISTRY.version || 'v'].join('|');
    var h = 2166136261;
    for (var i=0; i<s.length; i++) { h ^= s.charCodeAt(i); h += (h<<1)+(h<<4)+(h<<7)+(h<<8)+(h<<24); }
    return ((h >>> 0) % 1000) / 1000;
  }

  function chooseIcons(ctx) {
    var picked = [];
    var pickedIds = new Set();
    for (var i=0; i<ctx.count; i++) {
      var best = null, bestScore = -Infinity;
      ICONS.forEach(function (icon) {
        var s = scoreIcon(icon, ctx, pickedIds);
        if (s > bestScore) { bestScore = s; best = icon; }
      });
      if (!best || bestScore < -100) break;
      picked.push({ icon: best, score: bestScore });
      pickedIds.add(iconId(best));
      pickedIds.add(familyKey(best));
    }
    return picked;
  }

  function resolveLink(ctx, icon) {
    var href = ctx.href;
    var byKey = LINK_MAP.byKey || {};
    if (!href && ctx.linkKey && byKey[ctx.linkKey]) href = byKey[ctx.linkKey];
    if (!href && ctx.linkMode === 'auto') {
      var byCategory = LINK_MAP.byCategory || {};
      var byPillar = LINK_MAP.byPillar || {};
      var byWing = LINK_MAP.byWing || {};
      var c = slug(getField(icon, ['Primary Category'])) || slug(ctx.category).split('-')[0];
      if (byCategory[c] && byKey[byCategory[c]]) href = byKey[byCategory[c]];
      if (!href) {
        tokenList(ctx.pillars).some(function (p) { if (byPillar[p] && byKey[byPillar[p]]) { href = byKey[byPillar[p]]; return true; } });
      }
      if (!href) {
        var w = slug(ctx.pageWing);
        if (byWing[w] && byKey[byWing[w]]) href = byKey[byWing[w]];
      }
    }
    if (isSelfLink(href)) return '';
    return href || '';
  }

  function createIconNode(result, ctx, index) {
    var icon = result.icon;
    var url = iconUrl(icon);
    var id = iconId(icon);
    var fam = familyKey(icon);
    var img = document.createElement('img');
    img.className = 'jsi-icon jsi-tone--' + (ctx.renderTone || 'auto');
    img.src = url;
    img.decoding = 'async';
    img.loading = 'lazy';
    img.alt = ctx.alt || '';
    if (ctx.title) img.title = ctx.title;
    img.setAttribute('data-jsi-icon-id', id);
    img.setAttribute('data-jsi-family-key', fam);
    img.setAttribute('data-jsi-score', String(Math.round(result.score * 10) / 10));

    var wrapper = document.createElement('span');
    wrapper.className = [
      'jsi-icon-wrap',
      'jsi-place--' + ctx.placement,
      'jsi-container--' + ctx.container,
      'jsi-hierarchy--' + ctx.hierarchy,
      'jsi-treatment--' + ctx.treatment,
      'jsi-role--' + ctx.role,
      'jsi-owner--' + ctx.visualOwner
    ].join(' ');
    wrapper.setAttribute('data-jsi-placement', ctx.placement);
    wrapper.setAttribute('data-jsi-container', ctx.container);
    wrapper.setAttribute('data-jsi-treatment', ctx.treatment);
    wrapper.setAttribute('data-jsi-role', ctx.role);
    wrapper.setAttribute('data-jsi-hierarchy', ctx.hierarchy);
    if (ctx.size) wrapper.style.setProperty('--jsi-size', ctx.size);
    if (ctx.opacity) wrapper.style.setProperty('--jsi-opacity', ctx.opacity);
    wrapper.appendChild(img);

    var href = ctx.clickable ? resolveLink(ctx, icon) : '';
    if (href) {
      var a = document.createElement('a');
      a.href = href;
      a.className = 'jsi-icon-link';
      a.setAttribute('aria-label', ctx.linkLabel || ctx.title || 'Explore related JapanSolved page');
      a.appendChild(wrapper);
      return a;
    }
    return wrapper;
  }

  function markUsed(result) {
    var icon = result.icon;
    usedIconIds.add(iconId(icon));
    usedFamilyKeys.add(familyKey(icon));
    usedRotationGroups.add(rotationGroup(icon));
    usedVisualSubjects.add(slug(getField(icon, ['Primary Category'])));
  }

  function renderSlot(el) {
    if (el.getAttribute('data-jsi-rendered') === 'true') return;
    var ctx = readContext(el);
    if (ctx.noIconFromMap || ctx.treatment === 'no-icon' || ctx.placement === 'none' || ctx.role === 'none') {
      el.setAttribute('data-jsi-rendered', 'true');
      el.setAttribute('data-jsi-no-icon', 'true');
      el.style.display = 'none';
      return;
    }

    var choices = chooseIcons(ctx);
    el.innerHTML = '';
    el.classList.add('jsi-host', 'jsi-section--' + ctx.section, 'jsi-host-placement--' + ctx.placement);
    if (ctx.placement === 'upper-right-ghost' || ctx.placement === 'background-watermark') {
      var parent = el.parentElement;
      if (parent) parent.classList.add('jsi-host-has-ghost');
    }

    choices.forEach(function (result, i) {
      el.appendChild(createIconNode(result, ctx, i));
      markUsed(result);
    });

    el.setAttribute('data-jsi-rendered', 'true');
    if (debugMode) {
      el.setAttribute('data-jsi-debug-section', ctx.section);
      el.setAttribute('data-jsi-debug-placement', ctx.placement);
      el.setAttribute('data-jsi-debug-role', ctx.role);
      el.setAttribute('data-jsi-debug-page', ctx.pageHandle || '');
      el.setAttribute('data-jsi-debug-zone', ctx.zoneKey || '');
      if (choices[0]) el.setAttribute('data-jsi-debug-icon', iconId(choices[0].icon));
      console.info('[JSI v4.1]', ctx, choices.map(function (c) { return { id: iconId(c.icon), score: c.score }; }));
    }
  }

  function init() {
    if (!ICONS.length) {
      if (debugMode) console.warn('[JSI v4.1] No icons found in registry.');
      return;
    }
    document.querySelectorAll(SELECTOR).forEach(renderSlot);
    document.documentElement.setAttribute('data-jsi-icon-brain', 'v4.1');
    document.documentElement.setAttribute('data-jsi-icon-page-intent-map', PAGE_MAP ? 'sitewide-v1' : 'none');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
